import {
  useRef,
  useCallback,
  useEffect,
  useContext
} from 'react'

import { getIntItem } from './js/data-providers'

import useBasicWebsocket from '../use-chat/useBasicWebsocket'

function useChat (settings) {
  const worker = settings.worker
  const chatServiceUrl = settings.chatServiceUrl || 'localhost:8002/api/chat-service/a'
  const packet_code = settings.packet_code
  const sessionID = settings.session_id
  const preventCriteriaPrompts = settings.preventCriteriaPrompts
  const pageLoadID = settings.page_load_id
  const pageURL = settings.page_url
  const referrer = settings.referrer
  const pageTitle = settings.page_title
  const storagePrefix = settings.storagePrefix
  const messageCallback = settings.messageCallback
  const messageCallbackRef = useRef(null)
  messageCallbackRef.current = messageCallback
  const isReconnectRef = useRef(false)

  const {
    sendMessage,
    readyState,
    reconnect
  } = useBasicWebsocket({
    url: settings.wsURL ? settings.wsURL : `wss://${chatServiceUrl}/ws`,
    onMessage: ({ event }) => {
      try {
        const parsed = JSON.parse(event.data)
        if (parsed.type === 'ping') {
          worker.postMessage('ping')
          return
        }
      } catch (e) {
        console.log('received invalid json:', event.data)
      }
      messageCallbackRef.current(event)
    },
    reconnectDelayBackoff: 1.25,
    maxReconnectDelay: 10000,
    shouldReconnect: () => {
      worker.postMessage('stop')
      isReconnectRef.current = true
      return true
    },
    readyToConnect: sessionID,
    onOpen: ({ event, socket }) => {
      if (event.target !== socket) {
        event.target.close(3002, 'targetting wrong socket')
        return
      }
      worker.postMessage('start')
      if (sessionID) {
        socket.send(
          JSON.stringify({
            type: 'id',
            session_id: sessionID,
            support_bot: settings.supportBot,
            packet_code: packet_code,
            plid: pageLoadID,
            url: pageURL,
            referrer,
            page_title: pageTitle,
            visit_id: settings.visit_id,
            open: settings.displayState === 'open',
            prevent_criteria_prompts: preventCriteriaPrompts,
            is_reconnect: isReconnectRef.current,
            active_conversation_id: getIntItem(`${storagePrefix}active-convo-id-${packet_code}`)
          })
        )
      }
      if (settings.identity) {
        socket.send(
          JSON.stringify({ type: 'identity', identity: settings.identity })
        )
      }
    }
  })

  worker.onmessage = (event) => {
    if (event.data === 'presence') {
      sendMessage({ type: 'presence' })
    } else if (event.data === 'reconnect') {
      reconnect()
    }
  }

  const connectionStatus = readyState

  const subscribeToConversations = useCallback((conversationIDs) => {
    setTimeout(() => {
      sendMessage({ type: 'subscribe', conversation_ids: conversationIDs })
    }, 1000)
  }, [sendMessage])

  const lastPageLoadIdRef = useRef(pageLoadID)
  // This message is sent twice
  useEffect(() => {
    if (storagePrefix !== 'formbot' && pageLoadID && lastPageLoadIdRef.current !== pageLoadID) {
      lastPageLoadIdRef.current = pageLoadID
      sendMessage({
        type: 'page_change',
        visit_id: settings.visit_id,
        plid: pageLoadID,
        url: pageURL,
        page_title: pageTitle,
        referrer,
        check: true
      })
    }
  }, [sendMessage, pageLoadID, pageTitle, pageURL])

  const syncURLchange = (url, pageTitle, plid, referrer) => {
    if (url && storagePrefix !== 'formbot') {
      sendMessage({
        type: 'page_change',
        url,
        visit_id: settings.visit_id,
        plid,
        page_title: pageTitle,
        referrer,
        active_conversation_id: getIntItem(`${storagePrefix}active-convo-id-${packet_code}`)
      })
    }
  }

  const syncScrollPercent = scroll_percent => {
    sendMessage({ type: 'scroll_percent', scroll_percent })
  }

  const setDebug = debug => {
    sendMessage({ type: 'debug', debug })
  }

  const setIdentity = useCallback(() => {
    if (settings.identity) {
      sendMessage({ type: 'identity', identity: settings.identity })
    }
  }, [sendMessage, settings.identity])

  const setMetaAttribute = (key, value) => {
    sendMessage({ type: 'attribute', key, value })
  }

  const requestBot = () => {
    sendMessage({ type: 'bot_request' })
  }

  return {
    connectionStatus,
    subscribeToConversations,
    syncURLchange,
    syncScrollPercent,
    setDebug,
    setIdentity,
    setMetaAttribute,
    requestBot
  }
}

export default useChat
